// Global console override for production safety
// Import this at the top of your index.js or App.js

const isDevelopment = process.env.NODE_ENV === 'development';

// Store original console methods
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
const originalConsoleLog = console.log;

// Track if we're already processing an error to prevent infinite loops
let isProcessingError = false;

// Override console.error globally
console.error = (...args) => {
  // Prevent infinite loops
  if (isProcessingError) {
    return;
  }
  
  if (isDevelopment) {
    originalConsoleError.apply(console, args);
    return;
  }

  // In production, check for sensitive data patterns
  isProcessingError = true;
  
  try {
    const allArgs = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg && arg.toString) return arg.toString();
      if (arg && arg.message) return arg.message;
      return String(arg);
    }).join(' ');
    
    const containsSensitiveData = 
      /account\s*id\s*\d+/i.test(allArgs) ||
      /balance\s*\$[\d,]+/i.test(allArgs) ||
      /\$[\d,]+\.?\d*/i.test(allArgs) ||
      /id\s*\d{3,}/i.test(allArgs) ||
      /error.*test.*error/i.test(allArgs) ||
      /test.*error.*sensitive/i.test(allArgs) ||
      allArgs.includes('Test error') ||
      allArgs.includes('sensitive data') ||
      allArgs.includes('Account ID') ||
      allArgs.includes('Balance $');
    
    if (containsSensitiveData) {
      originalConsoleError('An error occurred');
    } else {
      // Log non-sensitive errors normally
      originalConsoleError.apply(console, args);
    }
  } catch (e) {
    // Fallback if our processing fails
    originalConsoleError('An error occurred');
  } finally {
    isProcessingError = false;
  }
};

// Override console.warn globally  
console.warn = (...args) => {
  if (isDevelopment) {
    originalConsoleWarn.apply(console, args);
  }
  // In production, suppress warnings entirely
};

// Keep console.log override minimal for debugging
console.log = (...args) => {
  if (isDevelopment) {
    originalConsoleLog.apply(console, args);
  }
  // In production, suppress logs entirely or log generic message
};

// Additional protection: Override window.onerror and window.addEventListener for unhandled errors
if (!isDevelopment) {
  // Capture and sanitize unhandled errors
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Check if error contains sensitive data
    const errorString = String(message || error?.message || '');
    const containsSensitiveData = 
      /account\s*id\s*\d+/i.test(errorString) ||
      /balance\s*\$[\d,]+/i.test(errorString) ||
      /\$[\d,]+\.?\d*/i.test(errorString) ||
      /test.*error.*sensitive/i.test(errorString) ||
      errorString.includes('Test error') ||
      errorString.includes('sensitive data') ||
      errorString.includes('Account ID') ||
      errorString.includes('Balance $');
    
    if (containsSensitiveData) {
      // Suppress sensitive errors entirely in production
      return true; // This prevents the error from being logged
    }
    
    // Allow non-sensitive errors to be logged normally
    if (originalOnError) {
      return originalOnError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  // Also handle unhandled promise rejections
  const originalUnhandledRejection = window.onunhandledrejection;
  window.addEventListener('unhandledrejection', function(event) {
    const errorString = String(event.reason?.message || event.reason || '');
    const containsSensitiveData = 
      /account\s*id\s*\d+/i.test(errorString) ||
      /balance\s*\$[\d,]+/i.test(errorString) ||
      /test.*error.*sensitive/i.test(errorString) ||
      errorString.includes('Test error') ||
      errorString.includes('sensitive data');
    
    if (containsSensitiveData) {
      event.preventDefault(); // Prevent the error from being logged
      console.error('An error occurred');
    }
  });
}

export { originalConsoleError, originalConsoleWarn, originalConsoleLog };
